import {
  listSparkProducts,
  startSparkPurchase,
  getPurchaseStatus,
  fetchSubscriptionPlans,
  fetchSubscriptionPlanContent,
  fetchTrialEligibility,
  fetchSubscriptionStatus,
  startSubscriptionCheckout,
  changeSubscriptionPlan,
  startPaidTrialBilling,
  getSubscriptionPortalUrl,
} from '../utils/billing.js';
import { getSubscriptionManagementTarget } from '../utils/subscription-ui.js';
import { reserveCheckoutWindow, navigateCheckoutWindow, closeCheckoutWindow } from '../utils/checkout-window.js';
import { auth } from './auth.svelte.js';
import { getAccountSnapshot, getBalance, isClientReady, revalidateSession, onAccountUpdate } from '../sogni-client.svelte.js';
import { notifyBillingRefresh } from '../utils/billing-events.js';

/**
 * Billing UI state — spark-pack purchases and Unlimited subscription.
 *
 * Stripe Checkout opens in a new tab; because the API's post-checkout page lives
 * on another *.sogni.ai origin, we refresh on window focus and on the
 * BroadcastChannel completion ping, and rely on the SDK socket to push the new
 * balance / entitlement.
 */

const SUBSCRIPTIONS_ENABLED = String(import.meta.env.VITE_SUBSCRIPTIONS_ENABLED || 'true') !== 'false';

export const SUBSCRIPTIONS_FEATURE_ENABLED = SUBSCRIPTIONS_ENABLED;

export const billing = $state({
  showPurchaseModal: false,
  showSubscriptionModal: false,

  // Spark packs
  products: [],
  productsLoading: false,
  productsError: null,
  purchasing: false,
  activePurchaseId: null,
  purchaseStatus: null, // 'processing' | 'completed' | 'expired' | null
  purchaseCheckoutUrl: null,
  purchaseError: null,
  purchaseContext: null,

  // Subscription
  plans: [],
  plansLoading: false,
  plansError: null,
  planContent: null,
  trialEligible: false,
  trialEligibility: 'loading', // loading | eligible | ineligible | error
  trialError: null,
  subscriptionError: null,
  subscriptionNotice: null,
  subscriptionCheckoutUrl: null,
  subscriptionCheckoutAccountId: null
});

let pollTimer = null;
let lastSubRefresh = 0;
let lastFocusRefresh = 0;
let billingAccountId = getAccountSnapshot().walletAddress?.toLowerCase() || null;

// Billing links survive a modal closing, so ownership lives with the global
// billing state rather than a mounted SubscriptionModal.
onAccountUpdate(snapshot => {
  const nextAccountId = snapshot.authenticated ? snapshot.walletAddress?.toLowerCase() || null : null;
  if (nextAccountId === billingAccountId) return;
  billingAccountId = nextAccountId;
  resetSubscriptionCheckout();
});

/* ---------------- Modals ---------------- */

export function openPurchaseModal(context = null) {
  // Reset any prior purchase state so a reopened modal starts clean.
  billing.purchaseStatus = null;
  billing.activePurchaseId = null;
  billing.purchaseCheckoutUrl = null;
  billing.purchaseError = null;
  billing.purchaseContext = context;
  billing.showPurchaseModal = true;
  loadProducts();
}
export function closePurchaseModal() {
  billing.showPurchaseModal = false;
}
export function openSubscriptionModal() {
  resetSubscriptionCheckout();
  billing.showSubscriptionModal = true;
  loadPlans();
}
export function resetSubscriptionCheckout() {
  billing.subscriptionError = null;
  billing.subscriptionNotice = null;
  billing.subscriptionCheckoutUrl = null;
  billing.subscriptionCheckoutAccountId = null;
}
export function getCurrentSubscriptionCheckoutUrl() {
  const snapshot = getAccountSnapshot();
  const currentId = snapshot.authenticated ? snapshot.walletAddress?.toLowerCase() : null;
  return currentId && currentId === billing.subscriptionCheckoutAccountId ? billing.subscriptionCheckoutUrl : null;
}
export function closeSubscriptionModal() {
  billing.showSubscriptionModal = false;
}

/* ---------------- Spark packs ---------------- */

export async function loadProducts() {
  if (billing.productsLoading) return;
  billing.productsLoading = true;
  billing.productsError = null;
  try {
    billing.products = await listSparkProducts();
  } catch (error) {
    console.error('Failed to load spark packs:', error);
    billing.productsError = 'Could not load spark packs. Please try again.';
  } finally {
    billing.productsLoading = false;
  }
}

export async function buyPack(productId) {
  if (billing.purchasing) return;
  billing.purchasing = true;
  billing.purchaseStatus = null;
  billing.activePurchaseId = null;
  billing.purchaseError = null;
  billing.purchaseCheckoutUrl = null;
  const forUser = auth.username;
  const tab = reserveCheckoutWindow();
  try {
    await verifyBillingAccount(forUser);
    const { url, purchaseId } = await startSparkPurchase(productId);
    if (!url) throw new Error('No checkout URL returned');
    if (!auth.isLoggedIn || auth.username !== forUser) throw new Error('Your account changed while checkout was opening. Review your current account before purchasing.');
    billing.activePurchaseId = purchaseId || null;
    billing.purchaseCheckoutUrl = url;
    navigateCheckoutWindow(tab, url);
    billing.purchaseStatus = 'processing';
    if (purchaseId) startPollingPurchase(purchaseId, forUser);
  } catch (error) {
    closeCheckoutWindow(tab);
    console.error('Failed to start spark purchase:', error);
    if (auth.username === forUser) {
      billing.purchaseStatus = 'error';
      billing.purchaseError = error.message || 'Could not start checkout. Please try again.';
    }
  } finally {
    billing.purchasing = false;
  }
}

function startPollingPurchase(purchaseId, forUser = auth.username) {
  stopPolling();
  const started = Date.now();
  const MAX_MS = 5 * 60 * 1000;
  pollTimer = setInterval(async () => {
    if (Date.now() - started > MAX_MS || auth.username !== forUser || billing.activePurchaseId !== purchaseId) {
      stopPolling();
      return;
    }
    try {
      const status = await getPurchaseStatus(purchaseId);
      if (auth.username !== forUser || billing.activePurchaseId !== purchaseId) return;
      billing.purchaseStatus = status;
      if (status === 'completed') {
        billing.purchaseError = null;
        stopPolling();
        await refreshBillingAccount();
      } else if (status === 'expired') {
        stopPolling();
      }
    } catch (error) {
      if (auth.username === forUser) billing.purchaseError = 'Could not verify the purchase yet. Refresh your account after checkout to check your balance.';
    }
  }, 3000);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

/* ---------------- Subscription ---------------- */

export async function loadPlans() {
  if (!SUBSCRIPTIONS_ENABLED || billing.plansLoading) return;
  billing.plansLoading = true;
  billing.plansError = null;
  try {
    const [plans, content] = await Promise.all([
      fetchSubscriptionPlans(),
      fetchSubscriptionPlanContent()
    ]);
    billing.plans = plans || [];
    billing.planContent = content;
  } catch (error) {
    console.error('Failed to load subscription plans:', error);
    billing.plansError = 'Could not load plans. Please try again.';
  } finally {
    billing.plansLoading = false;
  }
  // A member's entitlement can expire while this modal stays open.
  await refreshTrialEligibility();
}

export async function refreshTrialEligibility() {
  const forUser = auth.username;
  billing.trialEligibility = 'loading';
  billing.trialEligible = false;
  billing.trialError = null;
  try {
    if (!auth.isLoggedIn) throw new Error('Sign in to check free-trial eligibility.');
    const trial = await fetchTrialEligibility();
    if (auth.username !== forUser || !auth.isLoggedIn) return;
    billing.trialEligible = trial.eligible === true;
    billing.trialEligibility = trial.eligible ? 'eligible' : 'ineligible';
    return billing.trialEligible;
  } catch (error) {
    if (auth.username !== forUser) return;
    billing.trialEligibility = 'error';
    billing.trialError = error.message || 'Could not check trial eligibility. Please try again.';
    return false;
  }
}

async function verifyBillingAccount(forUser) {
  if (!await revalidateSession() || !auth.isLoggedIn || auth.username !== forUser) {
    throw new Error('Your signed-in account changed or could not be verified. Review your account before continuing.');
  }
}

function finishSubscriptionCheckout(tab, url) {
  billing.subscriptionCheckoutUrl = url;
  billing.subscriptionCheckoutAccountId = getAccountSnapshot().walletAddress?.toLowerCase() || null;
  const opened = navigateCheckoutWindow(tab, url);
  billing.subscriptionNotice = opened
    ? 'Complete the reviewed billing step in the new tab. Your plan updates when you return.'
    : 'Your browser blocked the new tab. Use the secure billing link below to continue.';
}

async function runSubscriptionBillingAction(action) {
  const forUser = auth.username;
  resetSubscriptionCheckout();
  const tab = reserveCheckoutWindow();
  try {
    await verifyBillingAccount(forUser);
    const result = await action();
    if (!auth.isLoggedIn || auth.username !== forUser) {
      throw new Error('Your account changed while billing was opening. Review your current account before continuing.');
    }
    if (result?.url) finishSubscriptionCheckout(tab, result.url);
    else if (result?.action === 'noop') {
      closeCheckoutWindow(tab);
      billing.subscriptionNotice = result?.message || 'You are already on this plan.';
    } else {
      throw new Error('Billing did not return a confirmation link. Please try again.');
    }
    return result;
  } catch (error) {
    closeCheckoutWindow(tab);
    if (auth.username === forUser) {
      billing.subscriptionError = error.message || 'Could not open secure billing. Please try again.';
    }
    console.error('Subscription billing action failed:', error);
    return null;
  }
}

export async function subscribe(planId, term, options = {}) {
  if (billing.trialEligibility === 'loading' || billing.trialEligibility === 'error') {
    billing.subscriptionError = 'Check trial eligibility before starting checkout.';
    return null;
  }
  const result = await runSubscriptionBillingAction(async () => {
    if (options.startTrial && !await refreshTrialEligibility()) {
      throw new Error(billing.trialError || 'A free trial is no longer available. Review a paid subscription separately.');
    }
    const url = await startSubscriptionCheckout(planId, term, options);
    return { url };
  });
  if (result?.url && options.startTrial) {
    billing.subscriptionNotice += ' Continue only if Stripe confirms the free trial and $0 due now. If payment is due now, cancel and return here to review a paid subscription separately.';
  }
  return result;
}

export async function changePlan(planId, term, billingConsent) {
  return runSubscriptionBillingAction(() => changeSubscriptionPlan(planId, term, billingConsent));
}

export async function activatePaidTrial(expectedPlan, billingConsent) {
  const forUser = auth.username;
  billing.subscriptionError = null;
  try {
    await verifyBillingAccount(forUser);
    await fetchSubscriptionStatus();
    const current = auth.subscription;
    if (auth.username !== forUser || current?.provider !== 'stripe' || current?.status !== 'trialing'
      || current.tier !== expectedPlan.planId || current.term !== expectedPlan.term) {
      throw new Error('Your trial or plan changed. Review your current subscription before starting paid billing.');
    }
    const result = await startPaidTrialBilling(billingConsent);
    if (auth.username !== forUser) throw new Error('Your signed-in account changed while billing was being confirmed.');
    await refreshBillingAccount();
    return result;
  } catch (error) {
    if (auth.username === forUser) billing.subscriptionError = error.message || 'Could not start paid billing. Review your payment method before trying again.';
    return null;
  }
}

/** Read both balances and entitlement; a refresh never submits generation. */
export async function refreshBillingAccount({ verifySession = true } = {}) {
  const forUser = auth.username;
  if (!auth.isLoggedIn) throw new Error('Sign in to refresh your account.');
  if (verifySession) await verifyBillingAccount(forUser);
  const [balance] = await Promise.all([getBalance(), fetchSubscriptionStatus()]);
  if (!auth.isLoggedIn || auth.username !== forUser) throw new Error('Your signed-in account changed.');
  auth.balance = balance;
  notifyBillingRefresh(getAccountSnapshot().walletAddress?.toLowerCase());
  return { balance, subscription: auth.subscription };
}

export async function manageSubscription(subscription = auth.subscription) {
  const target = getSubscriptionManagementTarget(subscription);
  if (target.kind === 'none') return null;
  if (target.kind === 'external') {
    resetSubscriptionCheckout();
    finishSubscriptionCheckout(reserveCheckoutWindow(), target.url);
    return { url: target.url };
  }
  return runSubscriptionBillingAction(async () => {
    const url = await getSubscriptionPortalUrl(subscription);
    if (!url) throw new Error('Billing did not return a management link. Please try again.');
    return { url };
  });
}

export async function refreshSubscriptionThrottled(force = false) {
  if (!isClientReady()) return;
  const now = Date.now();
  if (!force && now - lastSubRefresh < 15000) return;
  lastSubRefresh = now;
  try {
    await fetchSubscriptionStatus();
  } catch {
    // entitlement stays stale-while-error
  }
}

/* ---------------- Cross-tab / focus wiring ---------------- */

export function initBilling() {
  if (typeof window === 'undefined') return;

  // Completion pings from the Stripe return page (same-origin tabs only).
  try {
    const channel = new BroadcastChannel('sogni-purchase-status');
    channel.addEventListener('message', (event) => {
      const type = event.data?.type;
      if (type === 'spark-purchase-complete') {
        refreshBillingAccount().catch((error) => { billing.purchaseError = error.message; });
        if (billing.activePurchaseId) startPollingPurchase(billing.activePurchaseId);
      } else if (type === 'subscription-checkout-complete') {
        refreshBillingAccount().catch((error) => { billing.subscriptionError = error.message; });
      }
    });
  } catch {
    // BroadcastChannel unsupported — focus refresh still covers it.
  }

  // Catch Stripe redirect-backs — and cross-tab account switches — when the user
  // returns to this tab. Throttled so a user alt-tabbing doesn't hammer the API
  // (the SDK socket already pushes balance/entitlement live).
  const onFocus = async () => {
    if (!auth.isLoggedIn) return;
    // Re-probe identity first so a cross-tab account switch converges before we
    // apply balance/subscription (avoids showing user A with user B's balance).
    const sessionIsCurrent = await revalidateSession();
    if (!sessionIsCurrent) return;

    // Identity is never hidden behind the refresh throttle: the cookie may
    // switch between two quick focus events. Only the lower-risk balance and
    // subscription refreshes are throttled.
    const now = Date.now();
    if (now - lastFocusRefresh < 15000) return;
    lastFocusRefresh = now;
    try { await refreshBillingAccount({ verifySession: false }); }
    catch (error) { console.error('Billing account refresh failed:', error); }
  };
  window.addEventListener('focus', onFocus);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') onFocus();
  });

  // Handle a redirect-back landing on vibe with ?subscriptionCheckout=success|cancelled
  try {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('subscriptionCheckout');
    if (checkout) {
      params.delete('subscriptionCheckout');
      const qs = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (qs ? `?${qs}` : ''));
      if (checkout === 'success') setTimeout(() => refreshSubscriptionThrottled(true), 1000);
    }
  } catch {
    // ignore URL parsing issues
  }
}
